<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed } from 'vue'

import { SocialProvider } from '@xcel-next/rendering-tools/types/constants'
import { Environment } from '@xcel-next/core/enums'

import { useConfigStore, type RsConfig } from '../../stores/config'
import { usePageStore } from '../../stores/page'
import { useClient } from '../../utils/client-only'
import PinterestShareButton from '../shared/PinterestShareButton.vue'
import SocialShareLink from '../shared/SocialShareLink.vue'

import type { ComposedPageMeta } from '@xcel-next/composer-schema'

/**
 *
 * FIND THE LATEST SHARE URL STRUCTURE HERE: https://github.com/bradvin/social-share-urls
 *
 **/

// 'enableTopPosition' is a toggle, not a page type - the only real socialShare lists are article/horoscope
type SocialSharePageType = Exclude<keyof NonNullable<NonNullable<RsConfig['socialSettings']>['socialShare']>, 'enableTopPosition'>

const props = withDefaults(defineProps<{
  headline?: string,
  pageMeta?: ComposedPageMeta,
  pageType?: SocialSharePageType
}>(), {
  headline: '',
  pageType: 'article'
})

const { isClient } = useClient()

const { rsConfig } = storeToRefs(useConfigStore())
const { setPrintPreview } = usePageStore()

const socialShareList = computed(() => rsConfig.value.socialSettings?.socialShare?.[props.pageType] ?? [])
const mailtoConfig = computed(() => rsConfig.value.socialSettings?.mailto)
const shareUrl = computed(() => (rsConfig.value.socialSettings?.shareBaseUrl ?? '') + (props.pageMeta?.url ?? ''))

const facebookShareLink = computed(() => socialShareList.value.includes(SocialProvider.Facebook) ? 'https://www.facebook.com/sharer/sharer.php?u=' + encodeURIComponent(shareUrl.value) : false)
const flipboardShareLink = computed(() => socialShareList.value.includes(SocialProvider.Flipboad) ? 'https://share.flipboard.com/bookmarklet/popout?v=2&title=' + encodeURIComponent(props.headline) + '&url=' + encodeURIComponent(shareUrl.value) : false)
const whatsappShareLink = computed(() => socialShareList.value.includes(SocialProvider.WhatsApp) ? 'whatsapp://send?text=' + encodeURIComponent(shareUrl.value) : false)
const redditShareLink = computed(() => socialShareList.value.includes(SocialProvider.Reddit) ? 'https://reddit.com/submit?url=' + encodeURIComponent(shareUrl.value) + '&title=' + encodeURIComponent(props.headline) : false)
const mailtoShareLink = computed(() => socialShareList.value.includes(SocialProvider.MailTo) ? 'mailto:?subject=' + encodeURIComponent(mailtoConfig.value?.subject ?? '') + '&body=' + encodeURIComponent((mailtoConfig.value?.body ?? '').replace('%HEADLINE%', props.headline).replace('%URL%', shareUrl.value)) : false)

const isIos = () => !import.meta.env.SSR ? ['iPad', 'iPhone', 'iPod'].includes(navigator.platform) || (navigator.userAgent.includes('Mac') && 'ontouchend' in document) : false

type ShareItem =
  | { type: 'link', key: string, icon: string, href: string, text: string }
  | { type: 'pinterest' }
  | { type: 'print' }

// Renders in the exact order configured in socialShareList - pinterest/print can be placed anywhere in that list
const shareItems = computed<ShareItem[]>(() => {
  const items: ShareItem[] = []

  for (const provider of socialShareList.value) {
    switch (provider) {
      case SocialProvider.Facebook:
        if (facebookShareLink.value) items.push({ type: 'link', key: 'facebook', icon: 'facebook', href: facebookShareLink.value, text: 'Teilen' })
        break
      case SocialProvider.WhatsApp:
        if (isClient.value && isIos() && whatsappShareLink.value) items.push({ type: 'link', key: 'whatsapp', icon: 'whatsapp', href: whatsappShareLink.value, text: 'Teilen' })
        break
      case SocialProvider.Pinterest:
        items.push({ type: 'pinterest' })
        break
      case SocialProvider.Flipboad:
        if (flipboardShareLink.value) items.push({ type: 'link', key: 'flipboard', icon: 'flipboard', href: flipboardShareLink.value, text: 'Teilen' })
        break
      case SocialProvider.Reddit:
        if (redditShareLink.value) items.push({ type: 'link', key: 'reddit', icon: 'reddit', href: redditShareLink.value, text: 'Teilen' })
        break
      case SocialProvider.MailTo:
        if (mailtoShareLink.value) items.push({ type: 'link', key: 'mailto', icon: 'paperplane', href: mailtoShareLink.value, text: 'Versenden' })
        break
      case SocialProvider.Print:
        items.push({ type: 'print' })
        break
    }
  }
  return items
})

const showPrintPreview = () => {
  document.getElementsByTagName('body')[0].classList.add('bx-page--print')
  scroll(0, 0)

  if (!import.meta.env.SSR && process.env.NODE_ENV !== Environment.development) {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({
      event: 'custom_tracking_print_button'
    })
  }

  setPrintPreview(true)
}
</script>

<template>
  <div class="bx-content bx-social-share bx-typo--social-share">
    <div class="bx-social-share__element-list">
      <template v-for="item in shareItems"
                :key="item.type === 'link' ? item.key : item.type">
        <SocialShareLink v-if="item.type === 'link'"
                         :modifier-class="`bx-social-share__element--${item.key}`"
                         :icon="item.icon"
                         :href="item.href"
                         :text="item.text" />

        <PinterestShareButton v-else-if="item.type === 'pinterest'" />

        <div v-else
             class="bx-social-share__element--print">
          <div class="icon icon--social-share icon-print"
               tabindex="0"
               @click="showPrintPreview()">
            <svg><use href="#print" /></svg>
            <span class="bx-social-share__text">
              Drucken
            </span>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>
